import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  Menu,
  Modal,
  NavLink,
  Notification,
  Paper,
  SegmentedControl,
  Stepper,
  Table,
  Tabs,
  Tooltip,
  createTheme,
  rem,
  type CSSVariablesResolver,
} from '@mantine/core';

/**
 * The "editorial studio" theme: warm paper surfaces under ink-navy text, a serif display face for
 * headings and an iris accent used sparingly. Personality lives here and in global.css so the
 * screens themselves stay dense and legible — this is still a back-office tool full of tables and
 * status chips. Two things deliberately do not inherit any of it: the signature designer's canvas
 * and the preview iframe, which must look like the email they stand in for, not like the app
 * around them.
 *
 * Semantic colours are unchanged and load-bearing across ~40 status surfaces:
 * red=fail, orange=bypass/danger, yellow=warn/unsaved, green/teal=pass/active,
 * blue=info/current-step, gray=neutral/not-run. The iris primary was chosen not to collide.
 */
export const theme = createTheme({
  // Paper and ink. `white` becomes --mantine-color-body (Card/Modal/header backgrounds);
  // the page ground behind the paper is --portal-ground from the resolver below.
  white: '#fdfcf9',
  black: '#1d2333',

  primaryColor: 'iris',
  primaryShade: { light: 6, dark: 5 },
  autoContrast: true,

  colors: {
    // Electric indigo-violet, custom-built so shade 6 clears WCAG AA on paper.
    iris: [
      '#f2f0ff',
      '#e5e0ff',
      '#cdc2ff',
      '#b19eff',
      '#977dff',
      '#8461fb',
      '#7a52f4',
      '#6742e0',
      '#5636bf',
      '#472c9c',
    ],
    // Warm stone replaces the stock blue-gray: hairlines, dimmed text and striped rows all
    // pick these up through Mantine's derived variables.
    gray: [
      '#f7f5f1',
      '#efece6',
      '#e3dfd7',
      '#d3cec4',
      '#b8b2a7',
      '#9c968a',
      '#6e6960',
      '#565149',
      '#3c3934',
      '#262421',
    ],
    // Ink-slate for dark mode: 7 is the card surface, 8 the page ground, 0 the text.
    dark: [
      '#cdd0e0',
      '#b3b7cc',
      '#8e93ad',
      '#666c85',
      '#454b63',
      '#363c52',
      '#2b3045',
      '#20243a',
      '#191c2e',
      '#121420',
    ],
  },

  fontFamily:
    "'Schibsted Grotesk Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMonospace:
    "'JetBrains Mono Variable', ui-monospace, 'Cascadia Code', Consolas, monospace",
  headings: {
    fontFamily: "'Fraunces Variable', Georgia, 'Times New Roman', serif",
    // Fraunces is a variable face; 560 sits between semibold and bold with a bookish warmth.
    fontWeight: '560',
    textWrap: 'balance',
    sizes: {
      h1: { fontSize: rem(34), lineHeight: '1.2' },
      h2: { fontSize: rem(27), lineHeight: '1.25' },
      h3: { fontSize: rem(21), lineHeight: '1.3' },
      h4: { fontSize: rem(17), lineHeight: '1.35' },
      h5: { fontSize: rem(15), lineHeight: '1.4' },
      h6: { fontSize: rem(13), lineHeight: '1.4' },
    },
  },

  defaultRadius: 'md',
  // Warm-tinted layered shadows: a tight contact shadow plus a soft ambient one.
  shadows: {
    xs: '0 1px 2px rgba(29, 26, 18, 0.05), 0 1px 1px rgba(29, 26, 18, 0.03)',
    sm: '0 1px 2px rgba(29, 26, 18, 0.05), 0 6px 14px -4px rgba(29, 26, 18, 0.08)',
    md: '0 2px 4px rgba(29, 26, 18, 0.05), 0 14px 28px -8px rgba(29, 26, 18, 0.10)',
    lg: '0 2px 4px rgba(29, 26, 18, 0.06), 0 24px 40px -10px rgba(29, 26, 18, 0.14)',
    xl: '0 3px 6px rgba(29, 26, 18, 0.07), 0 36px 60px -12px rgba(29, 26, 18, 0.18)',
  },

  respectReducedMotion: true,
  cursorType: 'pointer',

  components: {
    Button: Button.extend({ defaultProps: { radius: 'md' } }),
    ActionIcon: ActionIcon.extend({ defaultProps: { radius: 'md' } }),
    Paper: Paper.extend({ defaultProps: { radius: 'md' } }),
    Card: Card.extend({ defaultProps: { radius: 'md', shadow: 'xs' } }),
    Modal: Modal.extend({
      defaultProps: {
        radius: 'lg',
        shadow: 'xl',
        padding: 'lg',
        overlayProps: { backgroundOpacity: 0.4, blur: 4 },
      },
      styles: {
        title: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: 600,
          fontSize: rem(18),
        },
      },
    }),
    Drawer: Drawer.extend({
      defaultProps: { overlayProps: { backgroundOpacity: 0.4, blur: 4 } },
      styles: {
        title: {
          fontFamily: 'var(--mantine-font-family-headings)',
          fontWeight: 600,
          fontSize: rem(18),
        },
      },
    }),
    Table: Table.extend({
      defaultProps: { striped: true, highlightOnHover: true, verticalSpacing: 'xs' },
      styles: {
        th: {
          textTransform: 'uppercase',
          fontSize: rem(11),
          letterSpacing: '0.06em',
          fontWeight: 600,
          color: 'var(--mantine-color-dimmed)',
        },
      },
    }),
    Tabs: Tabs.extend({ styles: { tab: { fontWeight: 500 } } }),
    NavLink: NavLink.extend({
      styles: {
        root: { borderRadius: 'var(--mantine-radius-sm)' },
        label: { fontWeight: 500 },
      },
    }),
    Badge: Badge.extend({ styles: { root: { letterSpacing: '0.02em' } } }),
    Tooltip: Tooltip.extend({ defaultProps: { withArrow: true, radius: 'sm' } }),
    Menu: Menu.extend({ defaultProps: { radius: 'md', shadow: 'md' } }),
    Alert: Alert.extend({ defaultProps: { radius: 'md' } }),
    Accordion: Accordion.extend({ defaultProps: { radius: 'md' } }),
    Notification: Notification.extend({
      defaultProps: { radius: 'md' },
      styles: { title: { fontWeight: 600 } },
    }),
    SegmentedControl: SegmentedControl.extend({ defaultProps: { radius: 'md' } }),
    Stepper: Stepper.extend({ styles: { stepLabel: { fontWeight: 500 } } }),
  },
});

/**
 * Custom variables Mantine does not derive on its own. The ground sits one step below the paper
 * cards so surfaces read as sheets on a desk; the glow is the iris atmosphere behind the auth
 * screens. Both flip with the colour scheme.
 */
export const cssVariablesResolver: CSSVariablesResolver = (resolvedTheme) => ({
  variables: {},
  light: {
    '--portal-ground': resolvedTheme.colors.gray[1],
    '--portal-hairline': resolvedTheme.colors.gray[2],
    '--portal-glow': 'rgba(122, 82, 244, 0.06)',
  },
  dark: {
    '--portal-ground': resolvedTheme.colors.dark[8],
    '--portal-hairline': resolvedTheme.colors.dark[5],
    '--portal-glow': 'rgba(151, 125, 255, 0.05)',
  },
});
